// ChipsInput — array-of-strings editor (ENG-843). Each chip is one array
// element; there are no separators, so commas/slashes inside a committed chip
// are plain content ("24/7 support" is representable).
//
// Interactions (from the approved ENG-843 prototype):
//   - Enter or comma commits the typed value (trim + trailing-comma strip —
//     see chipListEdit.js for the pure semantics)
//   - Backspace with an empty input removes the last chip
//   - × removes one chip; clicking the box focuses the input
//
// `chipStates[i]` = 'hit' | 'block' highlights a chip green/red while the
// live tester runs (PrefabTriggeringSection drives it).

import React, { useRef } from 'react'
import { addChip, removeChipAt, popChip } from '../lib/chipListEdit.js'

const ACCENT = '#8b6cff'
const GREEN = '#5ec98a'
const RED = '#e05858'

const CHIP_LOOKS = {
  accent: { background: `${ACCENT}21`, borderColor: `${ACCENT}61`, color: '#b9a5ff' },
  muted: { background: 'rgba(255,255,255,.04)', borderColor: 'rgba(255,255,255,.14)', color: '#a8aab2' },
  hit: { background: 'rgba(94,201,138,.14)', borderColor: GREEN, color: GREEN },
  block: { background: 'rgba(224,88,88,.12)', borderColor: RED, color: RED },
}

export default function ChipsInput({ values, onChange, placeholder, ariaLabel, variant = 'accent', chipStates, disabled = false, testId }) {
  const inputRef = useRef(null)
  const list = Array.isArray(values) ? values : []

  const commit = () => {
    const input = inputRef.current
    if (!input) return
    const { list: next, added } = addChip(list, input.value)
    if (added) {
      input.value = ''
      onChange(next)
    }
  }

  const onKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      commit()
    } else if (e.key === 'Backspace' && !e.currentTarget.value && list.length) {
      onChange(popChip(list))
    }
  }

  return (
    <div
      data-testid={testId}
      onClick={(e) => { if (e.target === e.currentTarget) inputRef.current?.focus() }}
      style={{
        background: '#101116', border: '1px solid #ffffff12', borderRadius: 6,
        padding: 5, display: 'flex', flexWrap: 'wrap', gap: 5,
        cursor: disabled ? 'default' : 'text', opacity: disabled ? 0.55 : 1,
      }}
    >
      {list.map((value, i) => {
        const state = chipStates?.[i]
        const look = CHIP_LOOKS[state] ?? CHIP_LOOKS[variant]
        return (
          <span
            key={`${value}-${i}`}
            data-chip={value}
            data-state={state ?? ''}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              border: '1px solid', borderRadius: 99, padding: '2px 5px 2px 9px',
              fontSize: 11, fontFamily: 'var(--m-font)', lineHeight: 1.5,
              transition: 'border-color .12s, background .12s',
              ...look,
            }}
          >
            <span>{value}</span>
            <button
              type="button"
              aria-label={`Remove ${value}`}
              disabled={disabled}
              onClick={() => onChange(removeChipAt(list, i))}
              style={{
                all: 'unset', cursor: disabled ? 'default' : 'pointer',
                width: 14, height: 14, borderRadius: '50%',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, lineHeight: 1, color: 'inherit', opacity: 0.65,
              }}
            >×</button>
          </span>
        )
      })}
      <input
        ref={inputRef}
        aria-label={ariaLabel}
        placeholder={list.length ? undefined : placeholder}
        disabled={disabled}
        onKeyDown={onKeyDown}
        onBlur={commit}
        style={{
          flex: 1, minWidth: 110, background: 'none', border: 0, outline: 'none',
          color: '#ececef', fontSize: 11, fontFamily: 'var(--m-font)', padding: '3px 4px',
        }}
      />
    </div>
  )
}
